/**
 * Name matching for suggestions: a check payee against vendors, a deposit payer
 * against customers, or any name against a list of names.
 *
 * Same rules, thresholds and scores as gl-api's src/core/utility/name_match.py
 * (its docstring explains each rule); change both together. The two test files
 * share one example table.
 */

// Words that describe what kind of business a name is, not which one.
export const FILLER_WORDS = new Set([
  'the', 'and', 'of',
  'inc', 'incorporated', 'llc', 'llp', 'pllc', 'ltd', 'limited', 'co', 'corp', 'corporation', 'company', 'pc',
  'group', 'grp', 'services', 'service', 'svc', 'svcs', 'insurance', 'ins', 'office',
]);

// Short forms read as the word only when the other name spells that word out ("Cobb Co" / "Cobb County").
const ABBREVIATIONS: Record<string, string> = {
  co: 'county', cnty: 'county', dept: 'department', dpt: 'department', comm: 'commissioner',
  commr: 'commissioner', assn: 'association', intl: 'international', natl: 'national',
  mgmt: 'management', univ: 'university', hosp: 'hospital', ctr: 'center', twp: 'township',
  govt: 'government',
};

const US_STATES: Record<string, string> = {
  al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
  co: 'colorado', ct: 'connecticut', de: 'delaware', dc: 'district of columbia', fl: 'florida',
  ga: 'georgia', hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
  ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
  ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi', mo: 'missouri',
  mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire', nj: 'new jersey',
  nm: 'new mexico', ny: 'new york', nc: 'north carolina', nd: 'north dakota', oh: 'ohio',
  ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania', ri: 'rhode island', sc: 'south carolina',
  sd: 'south dakota', tn: 'tennessee', tx: 'texas', ut: 'utah', vt: 'vermont',
  va: 'virginia', wa: 'washington', wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
};

export const SUGGEST_SCORE = 0.6;
export const CLOSE_SCORE = 0.85;
const LEAD_NEEDED = 0.15;
const REWRITTEN_CAP = 0.95;

export type MatchConfidence = 'high' | 'medium' | 'low';

/** Compare-key: case, accents, apostrophes, punctuation, "&" vs "and" and spacing never tell names apart. */
export const normalizeName = (value: string | null | undefined): string =>
  `${value || ''}`.normalize('NFKD').replace(/[^\t\n\r -~]/g, '').toLowerCase()
    // A "?" is a letter lost to a bad encoding ("Coste?ita"), dropped rather than splitting the word.
    .replace(/['‘’`´.?]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^0-9a-z]+/g, ' ')
    .trim();

const core = (words: string[]) => {
  const kept = words.filter((w) => !FILLER_WORDS.has(w));
  // A name made only of filler ("Insurance") is still compared by what it has.
  return kept.length ? kept : words;
};

/** Python difflib.SequenceMatcher(None, a, b).ratio(), for the short strings names are (no autojunk). */
export const similarity = (a: string, b: string): number => {
  if (!a.length && !b.length) return 1;
  const matched = (alo: number, ahi: number, blo: number, bhi: number): number => {
    let besti = alo; let bestj = blo; let size = 0;
    for (let i = alo; i < ahi; i += 1) {
      for (let j = blo; j < bhi; j += 1) {
        let k = 0;
        while (i + k < ahi && j + k < bhi && a[i + k] === b[j + k]) k += 1;
        if (k > size) { besti = i; bestj = j; size = k; }
      }
    }
    if (!size) return 0;
    return size + matched(alo, besti, blo, bestj) + matched(besti + size, ahi, bestj + size, bhi);
  };
  return (2 * matched(0, a.length, 0, b.length)) / (a.length + b.length);
};

const wordScore = (a: string, b: string): number => {
  if (a === b) return 1;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length === 1) return long.startsWith(short) ? 0.85 : 0;
  const ratio = similarity(a, b);
  if (short.length >= 3 && long.startsWith(short)) return Math.max(0.85, ratio);
  // 0.8 keeps "Rosys" / "Rosis" and "Bryan" / "Brian" but not "Meat" / "Mart" or "Meat" / "East" (0.75).
  return ratio >= 0.8 ? ratio : 0;
};

const round4 = (n: number) => Math.round(n * 10000) / 10000;

/** `words` with abbreviations, state codes and initials spelled the way `other` writes them; true when anything changed. */
const rewrite = (words: string[], other: string[]): [string[], boolean] => {
  let changed = false;
  const out: string[] = [];
  words.forEach((word) => {
    const options = [ABBREVIATIONS[word] || '', FILLER_WORDS.has(word) ? '' : US_STATES[word] || ''];
    const spelled = options.find((o) => o && !other.includes(word) && o.split(' ').every((w) => other.includes(w)));
    if (spelled) {
      out.push(...spelled.split(' '));
      changed = true;
    } else {
      out.push(word);
    }
  });

  // A run of two or more of our words whose first letters are one of the other's words ("income tax services" -> "its").
  // Three letters at least: two-letter words ("mi", "de", "la") are too common to be initials ("multi inc" is not "mi").
  const initials = new Set(other.filter((w) => w.length >= 3 && w.length <= 6 && !out.includes(w)));
  const result: string[] = [];
  let i = 0;
  while (i < out.length) {
    let taken = 0;
    for (let n = Math.min(6, out.length - i); n > 1; n -= 1) {
      const acronym = out.slice(i, i + n).map((w) => w[0]).join('');
      if (initials.has(acronym)) { result.push(acronym); taken = n; changed = true; break; }
    }
    if (taken) { i += taken; } else { result.push(out[i]); i += 1; }
  }
  return [result, changed];
};

/** How close two names are, 0..1. */
export const matchScore = (name: string | null | undefined, candidate: string | null | undefined): number => {
  let wordsA = normalizeName(name).split(' ').filter(Boolean);
  let wordsB = normalizeName(candidate).split(' ').filter(Boolean);
  if (!wordsA.length || !wordsB.length) return 0;
  let changedA: boolean; let changedB: boolean;
  [wordsA, changedA] = rewrite(wordsA, wordsB);
  [wordsB, changedB] = rewrite(wordsB, wordsA);
  const score = wordMatchScore(wordsA, wordsB);
  return changedA || changedB ? Math.min(score, REWRITTEN_CAP) : score;
};

const wordMatchScore = (wordsA: string[], wordsB: string[]): number => {
  if (wordsA.join('') === wordsB.join('')) return 1;

  const coreA = core(wordsA); const coreB = core(wordsB);
  if (coreA.join('') === coreB.join('') || [...coreA].sort().join(' ') === [...coreB].sort().join(' ')) return 0.9;

  const [short, long] = coreA.length <= coreB.length ? [coreA, coreB] : [coreB, coreA];
  const free = long.map((_, i) => i);
  const pairs: Array<[number, number]> = [];
  // Whether any pair is a real word in common: the same word or a close spelling, three letters or more.
  let sharesWord = false;
  short.forEach((word) => {
    // Same pick as Python's max(): the first of the highest-scoring free words.
    let best = -1;
    free.forEach((i) => { if (best < 0 || wordScore(word, long[i]) > wordScore(word, long[best])) best = i; });
    if (best >= 0 && wordScore(word, long[best]) > 0) {
      pairs.push([best, wordScore(word, long[best])]);
      const other = long[best];
      sharesWord = sharesWord || (Math.min(word.length, other.length) >= 3 && (word === other || similarity(word, other) >= 0.8));
      free.splice(free.indexOf(best), 1);
    }
  });

  if (!pairs.length) return 0;
  const total = pairs.reduce((sum, [, s]) => sum + s, 0);
  if (pairs.length < short.length) {
    // Sharing only a short word ("Mi", "La"), an initial ("G" / "Georgia") or a word start
    // ("More" / "Morenas") is no evidence of the same name.
    if (!sharesWord) return 0;
    return round4(0.6 + (0.15 * total) / long.length);
  }

  const average = total / pairs.length;
  if (short.length === long.length) return round4(average);
  // Pairs are made in the shorter name's order, so in order means the paired positions only go up.
  const positions = pairs.map(([i]) => i);
  const leading = positions[0] === 0 && positions.every((p, n) => n === 0 || p > positions[n - 1]);
  return round4(average * (leading ? 0.85 : 0.75));
};

export const matchConfidence = (score: number): MatchConfidence =>
  (score >= 1 ? 'high' : score >= CLOSE_SCORE ? 'medium' : 'low');

export interface RankedMatch<T> { score: number; confidence: MatchConfidence; item: T }

/** Candidates worth suggesting, best first, ties by name. */
export const rankMatches = <T>(
  name: string | null | undefined,
  candidates: T[],
  getName: (item: T) => string | null | undefined,
  limit = 5,
): RankedMatch<T>[] => candidates
  .map((item) => ({ item, score: matchScore(name, getName(item)) }))
  .filter(({ score }) => score >= SUGGEST_SCORE)
  .sort((x, y) => {
    // Plain code-point order on ties, like Python's sort, so both ends list ties alike.
    const [nx, ny] = [`${getName(x.item) || ''}`.toLowerCase(), `${getName(y.item) || ''}`.toLowerCase()];
    return y.score - x.score || (nx < ny ? -1 : nx > ny ? 1 : 0);
  })
  .slice(0, limit)
  .map(({ item, score }) => ({ item, score, confidence: matchConfidence(score) }));

/**
 * The candidate to pre-select from rankMatches output, or null when no one clearly wins: a close match at least
 * 0.15 ahead of the next. `minScore` raises the bar (1 = exact only) for screens where a pre-selection is riskier.
 */
export const bestMatch = <T>(ranked: RankedMatch<T>[], minScore = CLOSE_SCORE): T | null => {
  if (!ranked.length || ranked[0].score < minScore) return null;
  const runnerUp = ranked.length > 1 ? ranked[1].score : 0;
  return ranked[0].score - runnerUp >= LEAD_NEEDED ? ranked[0].item : null;
};
